import React, { useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI Icons
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

// Components
import Button from '../../components/formElements/Button';
import { useAuth } from '../../context/AuthContext';
import Orders from './orders/Orders';
import Confirm from '../../components/modals/confirmModal/Confirm';
import Addresses from './Addresses';
import Returns from './returns/Returns';
import Seo from '../../components/seo/Seo';

function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeTab, setActiveTab] = useState('zamówienia');
  // Dodajemy stan do śledzenia błędów ładowania obrazu
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Ustawienie aktywnej zakładki na podstawie ścieżki
    const path = location.pathname.split('/').pop();
    if (path && path !== '/') {
      setActiveTab(path);
    }
  }, [location.pathname]);

  // Funkcja do renderowania nawigacji
  const renderNavItem = (
    path: string,
    label: string,
    icon: React.ReactNode,
    isEnabled = true,
  ) => {
    const isActive = activeTab === path;

    return (
      <motion.button
        disabled={!isEnabled}
        onClick={() => {
          navigate(`/profil/${path}`);
          setActiveTab(path);
        }}
        className={`
          flex items-center gap-3 px-4 py-3 rounded-lg w-full transition-all
          ${isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-gray-50'}
          ${!isEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        whileHover={
          isEnabled
            ? { scale: 1.02, backgroundColor: 'rgba(79, 70, 229, 0.08)' }
            : {}
        }
        whileTap={isEnabled ? { scale: 0.98 } : {}}
      >
        <div className={isActive ? 'text-primary' : 'text-gray-500'}>
          {icon}
        </div>
        <span className="text-sm">{label}</span>
        {!isEnabled && (
          <span className="text-xs ml-auto bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
            Wkrótce
          </span>
        )}
      </motion.button>
    );
  };

  return (
    <>
      <div className="mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Nagłówek profilu */}
        <div className="py-6 md:py-8 lg:py-10">
          <div className="mb-4 sm:mb-8">
            <motion.h1
              className="text-2xl sm:text-3xl font-bold text-gray-900"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Twój profil
            </motion.h1>
            <motion.p
              className="text-base text-gray-600 mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Zarządzaj swoimi zamówieniami i ustawieniami konta
            </motion.p>
          </div>

          {/* Karta użytkownika */}
          <motion.div
            className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-6 md:flex md:items-center md:justify-between mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex items-center gap-4">
              {user?.profilePicture && !imageError ? (
                <img
                  src={user.profilePicture}
                  alt={user?.name}
                  className="h-14 w-14 rounded-full object-cover border border-gray-200"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <AccountCircleIcon style={{ fontSize: '2rem' }} />
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {user?.name}
                </h2>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
            </div>
            <motion.div
              className="mt-4 md:mt-0"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                colorTheme="grayScale"
                size="small"
                onClick={() => setShowConfirmModal(true)}
                className="flex items-center gap-2"
              >
                <LogoutIcon />
                <span>Wyloguj</span>
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Główna zawartość */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Pasek nawigacyjny */}
          <motion.div
            className="col-span-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-3 sticky top-24">
              <h3 className="text-xs uppercase text-gray-500 font-semibold px-4 py-2">
                Nawigacja
              </h3>
              <div className="space-y-1 mt-2">
                {renderNavItem(
                  'zamówienia',
                  'Zamówienia',
                  <LocalShippingIcon fontSize="small" />,
                )}
                {renderNavItem(
                  'adresy',
                  'Adresy',
                  <AccountCircleIcon fontSize="small" />,
                )}
                {renderNavItem(
                  'zwroty',
                  'Zwroty',
                  <ReceiptLongIcon fontSize="small" />,
                )}
              </div>
            </div>
          </motion.div>

          {/* Treść główna */}
          <motion.div
            className="col-span-1 md:col-span-3 bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Routes>
                  <Route path="/" element={<Navigate to="zamówienia" />} />
                  <Route path="/zamówienia/*" element={<Orders />} />
                  <Route
                    path="/ulubione"
                    element={
                      <div className="p-12 text-center">
                        <div className="text-6xl mb-4 text-gray-300">⚙️</div>
                        <h3 className="text-xl font-medium text-gray-800">
                          Funkcjonalność w przygotowaniu
                        </h3>
                        <p className="text-gray-500 mt-2">
                          Ta funkcja będzie dostępna wkrótce.
                        </p>
                      </div>
                    }
                  />
                  {/* Dodajemy nowy route dla adresów */}
                  <Route path="/adresy/*" element={<Addresses />} />
                  <Route path="/zwroty/*" element={<Returns />} />
                  <Route
                    path="/listy"
                    element={
                      <div className="p-12 text-center">
                        <div className="text-6xl mb-4 text-gray-300">⚙️</div>
                        <h3 className="text-xl font-medium text-gray-800">
                          Funkcjonalność w przygotowaniu
                        </h3>
                        <p className="text-gray-500 mt-2">
                          Ta funkcja będzie dostępna wkrótce.
                        </p>
                      </div>
                    }
                  />
                  <Route
                    path="/ustawienia"
                    element={
                      <div className="p-12 text-center">
                        <div className="text-6xl mb-4 text-gray-300">⚙️</div>
                        <h3 className="text-xl font-medium text-gray-800">
                          Funkcjonalność w przygotowaniu
                        </h3>
                        <p className="text-gray-500 mt-2">
                          Ta funkcja będzie dostępna wkrótce.
                        </p>
                      </div>
                    }
                  />
                </Routes>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Modal potwierdzenia */}
        <Confirm
          message="Czy na pewno chcesz się wylogować?"
          onConfirm={() => {
            logout();
            navigate('/');
          }}
          onCancel={() => setShowConfirmModal(false)}
          onClose={() => setShowConfirmModal(false)}
          isOpen={showConfirmModal}
        />
      </div>
      <Seo title="Profil"></Seo>
    </>
  );
}

export default Profile;
